import { LogLevel } from './types';

/**
 * Configuration for a `Logger` instance.
 *
 * Resolved once at application start-up — typically from build-time env
 * variables (webpack `DefinePlugin` on web, `react-native-config` /
 * build-time injection on mobile). All fields are required so misconfiguration
 * is caught at the call site rather than producing partially-filled events.
 */
export type LoggerConfig = {
    /** Name of the front-end application/module. Becomes `appName` on every event. */
    appName: string;
    /** Build version / commit hash. Becomes `appVersion` on every event. */
    appVersion: string;
    /** Environment identifier (`dev | test | uat | prod`). */
    environment: string;
    /** Minimum severity that will be emitted. Events below this level are dropped cheaply. */
    minLevel: LogLevel;
    /** Master switch. When `false`, no transport is invoked regardless of level. */
    enabled: boolean;
    /**
     * When `true`, the `Logger` runs string fields through the PII sanitizer
     * before dispatching to transports. Defaults to `true` when omitted in
     * later phases; declared here so it is part of the contract.
     */
    redactPII?: boolean;
    /**
     * Client user-agent summary (e.g. `Chrome 120`). When set, the
     * {@link Logger} stamps it onto every event as `browser`. Resolved once
     * at app start-up (parse `navigator.userAgent` on web; static string on
     * mobile). Left undefined when the runtime cannot provide it.
     */
    browser?: string;
    /** Client OS summary (e.g. `macOS 14`, `iOS 17`). Same lifecycle as `browser`. */
    os?: string;
};

/**
 * Numeric ranking of log levels used for severity comparisons.
 * Higher number ⇒ more severe.
 */
export const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
    DEBUG: 10,
    INFO: 20,
    WARN: 30,
    ERROR: 40,
};
